import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FiEye, FiEyeOff } from "react-icons/fi";
import FadeLoader from "react-spinners/FadeLoader";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import AuthShell from "../components/auth/AuthShell";
import {
  clearOtpChallenge,
  customer_login,
  messageClear,
  resend_verification,
  setPendingEmail,
  retry_login_otp,
  verify_login_otp,
} from "../store/reducers/authReducer";

const Login = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    loader,
    successMessage,
    errorMessage,
    userInfo,
    verificationRequired,
    pendingEmail,
    otpRequired,
    otpMaskedIdentifier,
    otpResendCooldownSeconds,
  } = useSelector((state) => state.auth);

  const [state, setState] = useState({
    email: pendingEmail || "",
    password: "",
  });
  const [loginMethod, setLoginMethod] = useState("password");
  const [showPassword, setShowPassword] = useState(false);
  const [otp, setOtp] = useState("");
  const [otpError, setOtpError] = useState("");
  const [resendCooldown, setResendCooldown] = useState(0);
  const sellerLoginUrl = "https://seller.myhaat24.com/login";

  const inputHandle = (e) => {
    setState({
      ...state,
      [e.target.name]: e.target.value,
    });
  };

  const login = (e) => {
    e.preventDefault();
    const identifier = state.email.trim();
    dispatch(setPendingEmail(identifier.includes("@") ? identifier : ""));
    dispatch(customer_login({
      identifier,
      loginMethod,
      ...(loginMethod === "password" ? { password: state.password } : {}),
    }));
  };

  const verifyOtp = (e) => {
    e.preventDefault();
    const normalizedOtp = otp.replace(/\D/g, "");

    if (!normalizedOtp) {
      setOtpError("Enter the OTP to continue");
      return;
    }

    setOtpError("");
    dispatch(verify_login_otp({ otp: normalizedOtp }));
  };

  const resendOtp = () => {
    if (resendCooldown > 0) return;
    dispatch(retry_login_otp());
  };

  const changeCredentials = () => {
    setOtp("");
    setOtpError("");
    dispatch(clearOtpChallenge());
  };

  const resendVerificationEmail = () => {
    if (!state.email) {
      toast.error("Enter your email first");
      return;
    }
    dispatch(resend_verification({ email: state.email }));
  };

  useEffect(() => {
    setResendCooldown(otpResendCooldownSeconds || 0);
  }, [otpResendCooldownSeconds]);

  useEffect(() => {
    if (!resendCooldown) return undefined;
    const timer = setInterval(() => {
      setResendCooldown((current) => (current > 0 ? current - 1 : 0));
    }, 1000);

    return () => clearInterval(timer);
  }, [resendCooldown]);

  useEffect(() => {
    if (successMessage) {
      toast.success(successMessage);
      dispatch(messageClear());
    }
    if (errorMessage) {
      toast.error(errorMessage);
      dispatch(messageClear());
    }
    if (userInfo) {
      navigate("/");
    }
  }, [dispatch, errorMessage, navigate, successMessage, userInfo]);

  return (
    <>
      {loader ? (
        <div className="fixed inset-0 z-[999] flex items-center justify-center bg-[#38303033]">
          <FadeLoader color="#FF7A1A" />
        </div>
      ) : null}

      <AuthShell
        title="Login"
        subtitle="Login to manage orders, wishlist and checkout faster."
        footer={
          <>
            New to MyHaat?{" "}
            <Link className="font-medium text-[#FF7A1A]" to="/register">
              Create account
            </Link>
          </>
        }
      >
        <div className="mb-5 flex gap-2" role="group" aria-label="Login method">
          {[["password", "Password login"], ["otp", "Mobile OTP login"]].map(([method, label]) => (
            <button key={method} type="button" disabled={loader} aria-pressed={loginMethod === method}
              onClick={() => {
                changeCredentials();
                dispatch(messageClear());
                setLoginMethod(method);
                setState({ email: "", password: "" });
                setShowPassword(false);
              }}
              className={`flex-1 rounded-lg border px-3 py-3 text-sm font-semibold ${loginMethod === method ? "border-[#FF7A1A] bg-[#fffaf6] text-[#c2550a]" : "border-[#E6E1DA] text-slate-600"}`}>
              {label}
            </button>
          ))}
        </div>
        {!otpRequired ? (
        <form onSubmit={login} className="space-y-4 text-slate-600">
          <div>
            <label
              className="mb-2 block text-sm font-medium text-[#0F1C2E]"
              htmlFor="email"
            >
              {loginMethod === "otp" ? "Mobile number" : "Email or mobile number"}
            </label>
            <input
              id="email"
              name="email"
              onChange={inputHandle}
              placeholder={loginMethod === "otp" ? "Enter your registered mobile number" : "Enter email or mobile number"}
              type={loginMethod === "otp" ? "tel" : "text"}
              autoComplete="username"
              required
              value={state.email}
              className="h-11 w-full rounded-lg border border-[#E6E1DA] px-3 text-sm outline-none transition-colors focus:border-[#FF7A1A]"
            />
          </div>

          {loginMethod === "password" && <div>
            <div className="mb-2 flex items-center justify-between gap-3">
              <label
                className="block text-sm font-medium text-[#0F1C2E]"
                htmlFor="password"
              >
                Password
              </label>
              <Link
                className="text-sm font-medium text-[#FF7A1A]"
                to="/forgot-password"
              >
                Forgot password?
              </Link>
            </div>
            <div className="relative">
              <input
                id="password"
                name="password"
                onChange={inputHandle}
                placeholder="Enter your password"
                type={showPassword ? "text" : "password"}
                value={state.password}
                autoComplete="current-password"
                required
                className="h-11 w-full rounded-lg border border-[#E6E1DA] px-3 pr-10 text-sm outline-none transition-colors focus:border-[#FF7A1A]"
              />
              <button
                type="button"
                onClick={() => setShowPassword((prev) => !prev)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-700"
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? <FiEyeOff /> : <FiEye />}
              </button>
            </div>
          </div>}

          <button disabled={loader} className="h-11 w-full rounded-lg bg-[#FF7A1A] text-sm font-semibold text-white transition-colors hover:bg-[#e56f17]">
            {loginMethod === "otp" ? "Send mobile OTP" : "Login"}
          </button>

          <a
            className="flex h-11 w-full items-center justify-center rounded-lg border border-[#E6E1DA] text-sm font-semibold text-[#0F1C2E] transition-colors hover:border-[#FF7A1A] hover:text-[#FF7A1A]"
            href={sellerLoginUrl}
          >
            Become Seller
          </a>
        </form>
        ) : (
          <form onSubmit={verifyOtp} className="space-y-4 text-slate-600">
            <div className="rounded-lg border border-[#E6E1DA] bg-[#fffaf6] px-4 py-3 text-sm text-slate-600">
              OTP sent to{" "}
              <span className="font-medium text-[#0F1C2E]">
                {otpMaskedIdentifier || "your registered contact"}
              </span>
            </div>

            <div>
              <label
                className="mb-2 block text-sm font-medium text-[#0F1C2E]"
                htmlFor="loginOtp"
              >
                OTP
              </label>
              <input
                id="loginOtp"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={8}
                onChange={(e) => {
                  setOtp(e.target.value.replace(/\D/g, ""));
                  setOtpError("");
                }}
                placeholder="Enter OTP"
                type="text"
                value={otp}
                className="h-11 w-full rounded-lg border border-[#E6E1DA] px-3 text-sm outline-none transition-colors focus:border-[#FF7A1A]"
              />
            </div>

            {otpError ? (
              <div className="rounded-lg border border-[#ffd6bf] bg-[#fff8f2] px-3 py-2 text-sm text-[#c2550a]">
                {otpError}
              </div>
            ) : null}

            <button
              className="h-11 w-full rounded-lg bg-[#FF7A1A] text-sm font-semibold text-white transition-colors hover:bg-[#e56f17] disabled:opacity-70"
              disabled={loader}
            >
              {loader ? "Verifying..." : "Verify OTP"}
            </button>

            <button
              type="button"
              onClick={resendOtp}
              disabled={loader || resendCooldown > 0}
              className="h-11 w-full rounded-lg border border-[#E6E1DA] text-sm font-semibold text-[#0F1C2E] transition-colors hover:border-[#FF7A1A] hover:text-[#FF7A1A] disabled:cursor-not-allowed disabled:opacity-60"
            >
              {resendCooldown > 0 ? `Resend OTP in ${resendCooldown}s` : "Resend OTP"}
            </button>

            <button
              type="button"
              onClick={changeCredentials}
              className="w-full text-sm font-medium text-[#FF7A1A] hover:text-[#e56f17]"
            >
              Change login details
            </button>
          </form>
        )}

        {verificationRequired && loginMethod === "password" ? (
          <div className="mt-5 rounded-lg border border-[#ffd6bf] bg-[#fff8f2] p-4 text-sm">
            <p className="font-medium text-[#c2550a]">
              Please verify your email before logging in.
            </p>
            <button
              className="mt-3 font-semibold text-[#FF7A1A] hover:text-[#e56f17]"
              onClick={resendVerificationEmail}
              type="button"
            >
              Resend verification email
            </button>
          </div>
        ) : null}
      </AuthShell>
    </>
  );
};

export default Login;
